import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import TestCaseRow from './TestCaseRow';
import MarkdownImportModal from './MarkdownImportModal';
import RunSettingsModal from './RunSettingsModal';
import {
  TEST_GRAPH_SETTINGS_LS_KEY,
  loadTestGraphSettings,
  saveTestGraphSettings,
  buildPipelineRunPayload,
  resolveLlmProvider,
} from '../../utils/pipelineRunPayload';
import './BuildGraph.css';
import './TestCasesPage.css';

function emptyDraft() {
  return {
    test_case_id: null,
    title: '',
    question: '',
    expected_answer: '',
    pass_threshold: 0.6,
  };
}

function TestCaseEditor({ initial, saving, onSave, onClose }) {
  const [draft, setDraft] = useState({ ...emptyDraft(), ...(initial || {}) });
  const [error, setError] = useState('');

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handleSave = () => {
    if (!draft.question?.trim() || !draft.expected_answer?.trim()) {
      setError('Question and Expected Answer are required.');
      return;
    }
    setError('');
    onSave({
      ...draft,
      pass_threshold: Math.max(0, Math.min(1, Number(draft.pass_threshold) || 0.6)),
    });
  };

  return (
    <div className="tc-modal-backdrop" onClick={onClose}>
      <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tc-modal-header">
          <h2>{draft.test_case_id ? 'Edit Test Case' : 'Add Test Case'}</h2>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
        <div className="tc-modal-body">
          <div className="tc-form-row">
            <div className="tc-form-field">
              <label>Title</label>
              <input
                value={draft.title || ''}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Geriatric patient — mechanical symptoms"
              />
            </div>
            <div className="tc-form-field">
              <label>Pass threshold ({Math.round((draft.pass_threshold ?? 0.6) * 100)}%)</label>
              <input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={draft.pass_threshold}
                onChange={(e) => update({ pass_threshold: e.target.value })}
              />
            </div>
          </div>
          <div className="tc-form-field">
            <label>Question</label>
            <textarea
              className="large"
              value={draft.question}
              onChange={(e) => update({ question: e.target.value })}
              placeholder="The natural language question to send to the graph"
            />
          </div>
          <div className="tc-form-field">
            <label>Expected Answer</label>
            <textarea
              className="large"
              value={draft.expected_answer}
              onChange={(e) => update({ expected_answer: e.target.value })}
              placeholder="The gold answer to compare against"
            />
          </div>
          {error && <div className="alert alert-error">{error}</div>}
        </div>
        <div className="tc-modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TestCasesPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();

  const [graph, setGraph] = useState(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError, setGraphError] = useState(null);

  const [testCases, setTestCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState(null);

  // Per-row run state: { [testCaseId]: { status, result } }
  const [rowState, setRowState] = useState({});
  const [expanded, setExpanded] = useState({});
  const [running, setRunning] = useState(false);

  const [compareWithLlm, setCompareWithLlm] = useState(false);
  const [defaultThreshold, setDefaultThreshold] = useState(0.6);

  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingCase, setSavingCase] = useState(false);

  // Pipeline run settings (shared with TestGraphPage via the same
  // localStorage key, so editing in either surface affects both).
  const [settings, setSettings] = useState(() => loadTestGraphSettings(graphId));
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const handleSettingsChange = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ---- Loaders ----------------------------------------------------------

  const loadGraph = useCallback(async () => {
    try {
      setGraphLoading(true);
      const resp = await apiService.getGraphDetails(graphId);
      if (resp.success) {
        setGraph(resp.graph);
      } else {
        setGraphError('Failed to load graph details');
      }
    } catch (err) {
      setGraphError(err.response?.data?.message || 'Failed to load graph details');
    } finally {
      setGraphLoading(false);
    }
  }, [graphId]);

  const loadCases = useCallback(async () => {
    try {
      setCasesLoading(true);
      setCasesError(null);
      const resp = await apiService.listGraphTestCases(graphId);
      if (resp.success) {
        setTestCases(resp.test_cases || []);
      } else {
        setCasesError(resp.error || 'Failed to load test cases');
      }
    } catch (err) {
      setCasesError(err.response?.data?.error || err.message || 'Failed to load test cases');
    } finally {
      setCasesLoading(false);
    }
  }, [graphId]);

  useEffect(() => { loadGraph(); }, [loadGraph]);
  useEffect(() => { loadCases(); }, [loadCases]);

  // Re-load settings when the graph changes.
  useEffect(() => {
    setSettings(loadTestGraphSettings(graphId));
  }, [graphId]);

  // Persist settings on change (same key TestGraphPage uses).
  useEffect(() => {
    saveTestGraphSettings(graphId, settings);
  }, [graphId, settings]);

  // Cross-page sync: pick up edits made on TestGraphPage in another tab.
  useEffect(() => {
    if (!graphId) return undefined;
    const key = TEST_GRAPH_SETTINGS_LS_KEY(graphId);
    const onStorage = (event) => {
      if (event.key !== key || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        setSettings((prev) => ({ ...prev, ...parsed, graph_id: graphId }));
      } catch (e) {
        // ignore parse errors
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [graphId]);

  // Auto-fill workspace_id once the graph row is loaded so payloads
  // built from a fresh localStorage have a workspace context.
  useEffect(() => {
    if (graph?.workspaceId && !settings.workspace_id) {
      setSettings((prev) => ({ ...prev, workspace_id: graph.workspaceId }));
    }
  }, [graph, settings.workspace_id]);

  // ---- CRUD -----------------------------------------------------------

  const handleSaveCase = async (draft) => {
    setSavingCase(true);
    try {
      let resp;
      if (draft.test_case_id) {
        resp = await apiService.updateGraphTestCase(draft.test_case_id, {
          title: draft.title,
          question: draft.question,
          expected_answer: draft.expected_answer,
          pass_threshold: draft.pass_threshold,
        });
      } else {
        resp = await apiService.createGraphTestCase({
          graph_id: graphId,
          workspace_id: graph?.workspaceId || null,
          title: draft.title,
          question: draft.question,
          expected_answer: draft.expected_answer,
          pass_threshold: draft.pass_threshold,
        });
      }
      if (!resp.success) throw new Error(resp.error || 'Save failed');
      setEditing(null);
      await loadCases();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Save failed';
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally {
      setSavingCase(false);
    }
  };

  const handleDelete = async (testCase) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete test case "${testCase.title || testCase.test_case_id}"? This will also delete its run history.`)) {
      return;
    }
    try {
      await apiService.deleteGraphTestCase(testCase.test_case_id);
      setRowState((s) => { const n = { ...s }; delete n[testCase.test_case_id]; return n; });
      await loadCases();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Delete failed';
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  };

  // ---- Run / scoring ---------------------------------------------------

  const runOne = useCallback(async (testCase, { useLlm } = {}) => {
    const tcId = testCase.test_case_id;
    setRowState((s) => ({ ...s, [tcId]: { status: 'running', result: null } }));
    const t0 = performance.now();
    try {
      const { providerId, rawModel } = resolveLlmProvider(settings);
      const payload = buildPipelineRunPayload({
        settings,
        nl_query: testCase.question,
        graph_id: graphId,
        workspace_id: graph?.workspaceId || settings.workspace_id,
        providerId,
        effectiveLlmModel: rawModel,
        perCaseConfig: testCase.execution_config,
      });
      const pipelineResp = await graphBuilderApi.runPipeline(payload);
      const actual = pipelineResp.natural_language_answer || '';

      let score;
      const scoringMethod = useLlm ? 'llm' : 'bm25';
      if (useLlm) {
        score = await graphBuilderApi.llmCompare({
          question: testCase.question,
          expected: testCase.expected_answer,
          actual,
        });
      } else {
        const corpus = testCases.map((c) => c.expected_answer).filter(Boolean);
        score = await graphBuilderApi.bm25Compare({
          expected: testCase.expected_answer,
          actual,
          corpus,
        });
      }

      const closeness = Number(score?.closeness ?? 0);
      const threshold = Number(testCase.pass_threshold ?? defaultThreshold);
      // exceeds_expected: LLM-judge bonus signal -- when true the rendered
      // answer adds correct, non-conflicting detail beyond EXPECTED. We
      // count that as an automatic PASS regardless of closeness so the
      // SNN platform isn't penalised for being more precise than the
      // (LLM-authored) expected answer. BM25 path never sets it -> false.
      const exceedsExpected = !!score?.exceeds_expected;
      const passed = closeness >= threshold || exceedsExpected;
      const durationMs = Math.round(performance.now() - t0);

      const result = {
        scoring_method: scoringMethod,
        actual_answer: actual,
        closeness,
        exceeds_expected: exceedsExpected,
        f1: score?.f1,
        precision: score?.precision,
        recall: score?.recall,
        bm25_score: score?.bm25_score,
        bm25_normalized: score?.bm25_normalized,
        missing_terms: score?.missing_terms || [],
        extra_terms: score?.extra_terms || [],
        token_counts: score?.token_counts || {},
        reasoning: score?.reasoning || '',
        pass: passed,
        duration_ms: durationMs,
      };

      setRowState((s) => ({
        ...s,
        [tcId]: { status: passed ? 'pass' : 'fail', result },
      }));

      // Fire-and-forget persist; do not block UI on failure.
      apiService.recordGraphTestRun({
        test_case_id: tcId,
        graph_id: graphId,
        scoring_method: scoringMethod,
        actual_answer: actual,
        closeness,
        exceeds_expected: exceedsExpected,
        precision: result.precision,
        recall: result.recall,
        f1: result.f1,
        bm25_score: result.bm25_score,
        bm25_normalized: result.bm25_normalized,
        missing_terms: result.missing_terms,
        extra_terms: result.extra_terms,
        token_counts: result.token_counts,
        llm_judge_reasoning: result.reasoning || null,
        pass: passed,
        duration_ms: durationMs,
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[TestCases] Failed to persist run:', err);
      });

      return result;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || err.message || 'Run failed';
      const errResult = { error: msg, scoring_method: useLlm ? 'llm' : 'bm25' };
      setRowState((s) => ({ ...s, [tcId]: { status: 'error', result: errResult } }));
      apiService.recordGraphTestRun({
        test_case_id: tcId,
        graph_id: graphId,
        scoring_method: useLlm ? 'llm' : 'bm25',
        error: String(msg),
        pass: false,
        duration_ms: Math.round(performance.now() - t0),
      }).catch(() => {});
      return null;
    }
  }, [graphId, settings, graph, testCases, defaultThreshold]);

  const runAll = useCallback(async () => {
    if (running || testCases.length === 0) return;
    setRunning(true);
    // Reset visible state
    const reset = {};
    testCases.forEach((tc) => { reset[tc.test_case_id] = { status: 'idle', result: null }; });
    setRowState(reset);
    try {
      for (const tc of testCases) {
        // eslint-disable-next-line no-await-in-loop
        await runOne(tc, { useLlm: compareWithLlm });
      }
    } finally {
      setRunning(false);
    }
  }, [running, testCases, runOne, compareWithLlm]);

  const runSingleRow = useCallback(async (tc) => {
    if (running) return;
    setRunning(true);
    try {
      await runOne(tc, { useLlm: compareWithLlm });
    } finally {
      setRunning(false);
    }
  }, [running, runOne, compareWithLlm]);

  const runSingleRowWithLlm = useCallback(async (tc) => {
    if (running) return;
    setRunning(true);
    try {
      await runOne(tc, { useLlm: true });
    } finally {
      setRunning(false);
    }
  }, [running, runOne]);

  // ---- Aggregate stats -------------------------------------------------

  const aggregate = useMemo(() => {
    let total = 0;
    let passed = 0;
    let scoreSum = 0;
    let scoreN = 0;
    testCases.forEach((tc) => {
      const r = rowState[tc.test_case_id]?.result || tc.latest_run;
      if (!r) return;
      total += 1;
      if (r.pass) passed += 1;
      if (typeof r.closeness === 'number') {
        scoreSum += r.closeness;
        scoreN += 1;
      }
    });
    return {
      total,
      passed,
      avg: scoreN > 0 ? scoreSum / scoreN : null,
    };
  }, [rowState, testCases]);

  const toggleExpand = useCallback((id) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  }, []);

  // ---- Render ---------------------------------------------------------

  if (graphLoading) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  if (graphError && !graph) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="page-header"><h1 className="page-title">Test Cases</h1></div>
        <div className="alert alert-error">{graphError}</div>
        <button className="btn-primary" onClick={() => navigate(`/user-home/graphs/build/${graphId}`)}>
          Back to Build Graph
        </button>
      </div>
    );
  }

  return (
    <div className="build-graph-page test-cases-page">
      <BuildGraphMenu workspaceId={graph?.workspaceId} />

      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Test Cases</h1>
          {graph && <p className="page-subtitle">{graph.graphName}</p>}
        </div>
        <div className="header-right">
          <button className="btn-secondary" onClick={() => navigate(`/user-home/graphs/build/${graphId}`)}>
            Back to Build Graph
          </button>
        </div>
      </div>

      {graph && (
        <div className="graph-info-card">
          <h3>Graph Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Workspace:</span>
              <span className="info-value">{graph.workspaceName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type:</span>
              <span className="info-value">{graph.graphType}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{graph.status}</span>
            </div>
          </div>
        </div>
      )}

      <div className="test-cases-toolbar">
        <div className="toolbar-group">
          <button
            className="btn-primary"
            onClick={() => setEditing(emptyDraft())}
            disabled={running}
          >
            + Add Test Case
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowImport(true)}
            disabled={running}
          >
            Import Markdown
          </button>
        </div>

        <div className="toolbar-spacer" />

        <div className="toolbar-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={compareWithLlm}
              onChange={(e) => setCompareWithLlm(e.target.checked)}
              disabled={running}
            />
            Compare with LLM
          </label>
          <label className="checkbox-label" title="Default pass threshold for newly added test cases">
            Default threshold
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={defaultThreshold}
              onChange={(e) => setDefaultThreshold(Math.max(0, Math.min(1, Number(e.target.value) || 0)))}
              disabled={running}
            />
          </label>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSettingsModalOpen(true)}
            disabled={running}
            title="Pick the same pipeline options used on the Test Graph page; applies to single Run and Run All Tests"
          >
            Run settings
          </button>
          <button
            className="btn-primary"
            onClick={runAll}
            disabled={running || testCases.length === 0}
          >
            {running ? 'Running...' : `Run All Tests (${testCases.length})`}
          </button>
        </div>
      </div>

      {(aggregate.total > 0) && (
        <div className="test-cases-summary">
          <span>
            <strong>Passed:</strong> {aggregate.passed} / {aggregate.total}
          </span>
          {aggregate.avg != null && (
            <span><strong>Avg closeness:</strong> {Math.round(aggregate.avg * 100)}%</span>
          )}
          <span>
            <strong>Scoring:</strong> {compareWithLlm ? 'LLM judge (slow)' : 'BM25 + IDF F1 (local, fast)'}
          </span>
        </div>
      )}

      {casesError && <div className="alert alert-error">{casesError}</div>}

      {casesLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading test cases...</p>
        </div>
      ) : testCases.length === 0 ? (
        <div className="test-cases-empty">
          No test cases yet. Click <strong>+ Add Test Case</strong> to create one,
          or <strong>Import Markdown</strong> to bulk-load Q&amp;A sets.
        </div>
      ) : (
        <div className="test-cases-list">
          {testCases.map((tc, i) => {
            const rs = rowState[tc.test_case_id] || {};
            return (
              <TestCaseRow
                key={tc.test_case_id}
                testCase={tc}
                index={i}
                status={rs.status}
                result={rs.result}
                expanded={!!expanded[tc.test_case_id]}
                onToggleExpand={toggleExpand}
                onRun={runSingleRow}
                onRunWithLlm={runSingleRowWithLlm}
                onEdit={(c) => setEditing({
                  test_case_id: c.test_case_id,
                  title: c.title || '',
                  question: c.question,
                  expected_answer: c.expected_answer,
                  pass_threshold: c.pass_threshold ?? 0.6,
                })}
                onDelete={handleDelete}
                isAnyRunning={running}
              />
            );
          })}
        </div>
      )}

      {showImport && (
        <MarkdownImportModal
          graphId={graphId}
          onClose={() => setShowImport(false)}
          onImported={async (committed, count) => {
            setShowImport(false);
            // eslint-disable-next-line no-alert
            alert(`Imported ${count} test case${count === 1 ? '' : 's'}.`);
            await loadCases();
          }}
        />
      )}

      {editing && (
        <TestCaseEditor
          initial={editing}
          saving={savingCase}
          onSave={handleSaveCase}
          onClose={() => { if (!savingCase) setEditing(null); }}
        />
      )}

      <RunSettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        graphId={graphId}
        workspaceId={graph?.workspaceId}
      />
    </div>
  );
}

export default TestCasesPage;
